import {
  StoreFilter,
  VariantRestrictionCondition,
} from "@CommonApi/merchflow/parse-variant-restrictions";
import { StoreFacetStore } from "@CommonApi/merchflow/store-facets";

export interface DraftRule {
  id: string;
  variant: string;
  /** false when the parsed variant didn't match a known variant for the category. */
  matched: boolean;
  suggestion?: string;
  bayNumberRule: string;
  shelfRule: string;
  condition: VariantRestrictionCondition;
  /** The parse pass (description) that produced this rule; absent for manual rules. */
  sourceDescriptionId?: string;
}

export interface DraftGroup {
  id: string;
  /** Raw, editable store-code text (comma or pipe separated). */
  storeInput: string;
  /** Selected store-filter values per dimension, chosen from the category's facets. */
  filter: StoreFilter;
  /** Store references the model couldn't resolve — shown as a warning until edited. */
  unmatchedStores: string[];
  rules: DraftRule[];
}

export const emptyStoreFilter = (): StoreFilter => ({
  bay_sizes: [],
  states: [],
  clusters: [],
  section_heights: [],
  section_widths: [],
});

export const isEmptyStoreFilter = (filter: StoreFilter): boolean =>
  filter.bay_sizes.length === 0 &&
  filter.states.length === 0 &&
  filter.clusters.length === 0 &&
  filter.section_heights.length === 0 &&
  filter.section_widths.length === 0;

/**
 * The token the bay/shelf "All" toggle emits, meaning every bay / every shelf.
 * The backend recognises this (see `isAllToken` in VariantRestrictionService)
 * and expands it across the store's fixtures.
 */
export const ALL_BAYS_SHELVES = "*";

/** Split the editable store-code text into codes; empty text = no scope (all stores). */
export const parseStoreCodes = (storeInput: string): string[] =>
  storeInput
    .split(/[,|]/)
    .map((code) => code.trim())
    .filter(Boolean);

/** Mirror of the backend matcher: a store matches every non-empty dimension. */
export const matchesStoreFilter = (store: StoreFacetStore, filter: StoreFilter): boolean => {
  if (filter.bay_sizes.length && !filter.bay_sizes.includes(store.bay_size)) return false;
  if (filter.states.length && !filter.states.includes(store.state)) return false;
  if (filter.clusters.length && !filter.clusters.includes(store.cluster)) return false;
  if (filter.section_heights.length && !filter.section_heights.includes(store.section_height)) {
    return false;
  }
  if (filter.section_widths.length && !filter.section_widths.includes(store.section_width)) {
    return false;
  }
  return true;
};

/** Store codes the user typed that don't correspond to a known category store. */
export const findUnmatchedStoreCodes = (
  storeInput: string,
  facetStores: StoreFacetStore[],
): string[] => {
  const known = new Set(facetStores.map((store) => store.code));
  return parseStoreCodes(storeInput).filter((code) => !known.has(code));
};

/**
 * How many stores a group's scope resolves to: explicit codes unioned with the
 * filter's matches. `all` is true when the scope is empty (every category store).
 * Only codes that match a known store are counted — arbitrary/unknown codes are
 * excluded here (and surfaced as unmatched elsewhere) so the count never
 * overstates the real scope.
 */
export const countScopedStores = (
  storeInput: string,
  filter: StoreFilter,
  facetStores: StoreFacetStore[],
): { all: boolean; count: number } => {
  const known = new Set(facetStores.map((store) => store.code));
  const rawCodes = parseStoreCodes(storeInput);
  const filterActive = !isEmptyStoreFilter(filter);

  if (rawCodes.length === 0 && !filterActive) {
    return { all: true, count: facetStores.length };
  }

  const scoped = new Set(rawCodes.filter((code) => known.has(code)));
  if (filterActive) {
    for (const store of facetStores) {
      if (matchesStoreFilter(store, filter)) scoped.add(store.code);
    }
  }
  return { all: false, count: scoped.size };
};
